import { useMemo } from "react";
import Alert from "@mui/material/Alert";
import SoftBox from "components/SoftBox";
import { useAuth } from "context/AuthContext";
import { getMenuTypeFromToken } from "layouts/dashboardLayout/config";
import AdminLeaderboardView from "./AdminLeaderboardView";
import SingleCategoryLeaderboardView from "./SingleCategoryLeaderboardView";
import {
  airportTabStats,
  demoAirports,
  demoNonprofits,
  demoPilots,
  findDemoAirportByEntityId,
  findDemoNonprofitByEntityId,
  nonprofitTabStats,
  pilotTabStats,
} from "data/leaderboardDemoData";

const pilotColumns = [
  { key: "_rank", label: "#", align: "center", width: 52 },
  { key: "_entity", label: "Pilot" },
  { key: "missions", label: "Missions" },
  { key: "hours", label: "Hours" },
  { key: "avgPts", label: "Avg Pts" },
  { key: "_score", label: "Score" },
  { key: "_action", label: "" },
];

const airportColumns = [
  { key: "_rank", label: "#", align: "center", width: 52 },
  { key: "_entity", label: "Airport" },
  { key: "missions", label: "Missions" },
  { key: "fuelGal", label: "Fuel (gal)" },
  { key: "_score", label: "Score" },
  { key: "_action", label: "" },
];

const nonprofitColumns = [
  { key: "_rank", label: "#", align: "center", width: 52 },
  { key: "_entity", label: "Organization" },
  { key: "missions", label: "Missions" },
  { key: "patients", label: "Patients" },
  { key: "_score", label: "Score" },
  { key: "_action", label: "" },
];

function LeaderboardsPage() {
  const { isGuest, tokenRole, tokenEntityType, tokenEntityId } = useAuth();

  const portalKind = useMemo(() => {
    if (isGuest) return "admin";
    const t = getMenuTypeFromToken(tokenRole, tokenEntityType);
    if (t === "user") return "pilot";
    return t;
  }, [isGuest, tokenRole, tokenEntityType]);

  const airportContext = useMemo(
    () => (!isGuest && portalKind === "airport" ? findDemoAirportByEntityId(tokenEntityId) : null),
    [isGuest, portalKind, tokenEntityId]
  );

  const nonprofitContext = useMemo(
    () => (!isGuest && portalKind === "nonprofit" ? findDemoNonprofitByEntityId(tokenEntityId) : null),
    [isGuest, portalKind, tokenEntityId]
  );

  return (
    <SoftBox py={1}>
      {isGuest && (
        <SoftBox mb={2}>
          <Alert severity="info">
            Guest mode shows illustrative leaderboard data only. Sign in to see live metrics for your
            organization.
          </Alert>
        </SoftBox>
      )}

      {portalKind === "admin" && <AdminLeaderboardView />}

      {portalKind === "pilot" && (
        <SingleCategoryLeaderboardView
          title="Pilot leaderboard"
          subtitle="See how volunteer pilots rank on missions, hours, and impact score"
          stats={pilotTabStats}
          tableTitle="Pilot rankings"
          searchPlaceholder="Search pilot…"
          columns={pilotColumns}
          rows={demoPilots}
          entityType="Pilot"
          filterKeys={["name", "sub", "location"]}
        />
      )}

      {portalKind === "airport" && (
        <SingleCategoryLeaderboardView
          title="Airport leaderboard"
          subtitle="Partner airport rankings by missions supported, fuel contributions, and impact"
          stats={airportTabStats}
          tableTitle="Airport rankings"
          searchPlaceholder="Search airport…"
          columns={airportColumns}
          rows={demoAirports}
          entityType="Airport"
          filterKeys={["name", "sub", "icao", "location"]}
          highlightBanner={
            airportContext
              ? `Sample highlight: metrics shown for ${airportContext.name} (${airportContext.icao}) match this portal’s entity id when present in demo data.`
              : "Compare your hub to other partner airports in the network (sample data until live metrics are connected)."
          }
        />
      )}

      {portalKind === "nonprofit" && (
        <SingleCategoryLeaderboardView
          title="Nonprofit leaderboard"
          subtitle="Program rankings by coordinated missions, patients served, and overall score"
          stats={nonprofitTabStats}
          tableTitle="Nonprofit rankings"
          searchPlaceholder="Search nonprofit…"
          columns={nonprofitColumns}
          rows={demoNonprofits}
          entityType="Nonprofit"
          filterKeys={["name", "sub", "location", "niche"]}
          highlightBanner={
            nonprofitContext
              ? `Sample highlight: ${nonprofitContext.name} is linked to your portal entity in demo data.`
              : "See how your organization compares across the AviPair nonprofit network (sample data)."
          }
        />
      )}
    </SoftBox>
  );
}

export default LeaderboardsPage;
